import messages from "messages";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  SidesheetType,
  selectCurrentSidesheet,
  selectFocusedBlockedInterval,
  showBlockIntervalDetails,
  showGcalEventDetails,
} from "store/calendarSidebarSlice";
import getClasses from "utils/getClasses";
import { useCalendarSidebarContext } from "../../../../../calendar-sidebar/context/CalendarSidebarContext";
import { BlockedIntervalEventType } from "../scrollable-calendar-view-header/ScrollableCalendarViewHeader.types";
import TimeslotButton from "../timeslot-button/TimeslotButton";
import styles from "./FullDayEventTimeslot.module.scss";

type Props = {
  event: CalendarBlockedInterval;
  eventType: BlockedIntervalEventType;
};
const FullDayEventTimeslot = ({ event, eventType }: Props) => {
  const dispatch = useDispatch();
  const { requestCancelAddNewSession } = useCalendarSidebarContext();

  const currentSidesheet = useSelector(selectCurrentSidesheet);
  const focusedBlockedInterval = useSelector(selectFocusedBlockedInterval);

  const title = useMemo(
    () => event.busy_note || `(${messages.noTitle})`,
    [event.busy_note],
  );

  const showCurrentEventDetails = useCallback(() => {
    if (eventType === "GCAL_EVENT") {
      dispatch(showGcalEventDetails(event));
    } else {
      dispatch(showBlockIntervalDetails(event));
    }
  }, [dispatch, event, eventType]);

  const onClick = useCallback(() => {
    if (
      currentSidesheet === SidesheetType.AddToCalendar &&
      requestCancelAddNewSession
    ) {
      requestCancelAddNewSession(showCurrentEventDetails);
    } else {
      showCurrentEventDetails();
    }
  }, [currentSidesheet, requestCancelAddNewSession, showCurrentEventDetails]);

  const isFocused = useMemo(
    () =>
      eventType === "BLOCKED_INTERVAL" &&
      focusedBlockedInterval?.timeslot_id === event.timeslot_id,
    [eventType, event.timeslot_id, focusedBlockedInterval],
  );

  const isPastBlockedInterval = useMemo(
    () =>
      eventType === "BLOCKED_INTERVAL" &&
      new Date(event.end).valueOf() < Date.now(),
    [eventType, event.end],
  );

  return (
    <TimeslotButton
      className={getClasses(
        styles["full-day-event-timeslot"],
        eventType === "BLOCKED_INTERVAL" &&
          styles["full-day-event-timeslot--blocked-interval"],
        isPastBlockedInterval &&
          styles["full-day-event-timeslot--past-blocked-interval"],
      )}
      onClick={onClick}
      timeslotName={title}
      showGCalIcon={eventType === "GCAL_EVENT"}
      isFocused={isFocused}
    />
  );
};

export default FullDayEventTimeslot;
